/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { User } from "@supabase/supabase-js";
import { classifyLocation } from "../config/domains";
import { isSupabaseConfigured, setSupabaseAccessToken, supabase } from "../lib/supabase";
import { authCallbackDestination, emptyAccessContext, normalizeAccessContext, postLoginDestination, type AccessContext, type TenantAccess } from "./access";
import { BrokerError, sessionBroker, type BrokerSession } from "./session-broker";

interface AuthResult { error: string | null; destination?: string }
export interface MfaStatus { aal: "aal1" | "aal2"; factors: Array<{ id: string; status: string; friendly_name?: string }> }
export interface MfaEnrollment { factor_id: string; qr_code: string }

interface AuthState {
  user: User | null;
  loading: boolean;
  configured: boolean;
  localDemo: boolean;
  accessContext: AccessContext;
  accessToken: string | null;
  signIn(email: string, password: string, returnTo?: string | null): Promise<AuthResult>;
  signOut(): Promise<void>;
  resetPassword(email: string): Promise<string | null>;
  completeAuthCallback(tokenHash: string, type: string, returnTo?: string | null): Promise<AuthResult>;
  updatePassword(password: string): Promise<AuthResult>;
  resolveTenantAccess(slug: string): Promise<TenantAccess>;
  refreshAccessContext(): Promise<AccessContext>;
  getMfaStatus(): Promise<MfaStatus>;
  enrollMfa(): Promise<MfaEnrollment>;
  challengeMfa(factorId: string): Promise<string>;
  verifyMfa(factorId: string, challengeId: string, code: string): Promise<AuthResult>;
}

const AuthContext = createContext<AuthState | null>(null);
const localDemo = import.meta.env.DEV && import.meta.env.VITE_ENABLE_LOCAL_DEMO === "true";

const demoUser = {
  id: "00000000-0000-4000-8000-000000000001",
  aud: "authenticated",
  role: "authenticated",
  email: "preview@local.invalid",
  app_metadata: {},
  user_metadata: { full_name: "Vista local" },
  created_at: new Date().toISOString(),
} as User;

const demoAccess: AccessContext = {
  isPositronicoOwner: true,
  ownerAuthorized: true,
  ownerMfaRequired: true,
  aal: "aal2",
  workspaces: [{ organizationId: "10000000-0000-4000-8000-000000000001", name: "DLR", slug: "dlr", role: "POSITRONICO_OWNER", status: "ACTIVE", organizationActive: true }],
};

let initialSessionPromise: Promise<BrokerSession> | null = null;
function initialSession() {
  if (!initialSessionPromise) initialSessionPromise = sessionBroker.session().finally(() => { window.setTimeout(() => { initialSessionPromise = null; }, 0); });
  return initialSessionPromise;
}

async function loadAccessContext(): Promise<AccessContext> {
  if (!supabase) return emptyAccessContext;
  const { data, error } = await supabase.rpc("get_access_context");
  if (error) throw error;
  return normalizeAccessContext(data);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(localDemo ? demoUser : null);
  const [loading, setLoading] = useState(!localDemo && isSupabaseConfigured);
  const [accessContext, setAccessContext] = useState<AccessContext>(localDemo ? demoAccess : emptyAccessContext);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const refreshTimer = useRef<number | null>(null);
  const surface = classifyLocation(window.location);

  const clearSession = useCallback(() => {
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
    setSupabaseAccessToken(null);
    setAccessToken(null);
    setCsrfToken(null);
    setAccessContext(emptyAccessContext);
    setUser(null);
  }, []);

  const acceptSession = useCallback(async (session: BrokerSession) => {
    setSupabaseAccessToken(session.accessToken);
    setAccessToken(session.accessToken);
    setCsrfToken(session.csrfToken);
    let context = emptyAccessContext;
    if (surface.kind !== "public") context = await loadAccessContext();
    setAccessContext(context);
    setUser(session.user);
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => {
      void sessionBroker.session().then(acceptSession).catch(clearSession);
    }, Math.max(30, session.expiresIn - 60) * 1000);
    return context;
  }, [clearSession, surface.kind]);

  useEffect(() => {
    if (localDemo || !isSupabaseConfigured) { setLoading(false); return; }
    let active = true;
    void initialSession()
      .then((session) => active ? acceptSession(session) : undefined)
      .catch((error) => { if (active && (!(error instanceof BrokerError) || error.status !== 401)) console.warn("Session bootstrap failed", error); if (active) clearSession(); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [acceptSession, clearSession]);

  const refreshAccessContext = useCallback(async () => {
    if (localDemo) { setAccessContext(demoAccess); return demoAccess; }
    const context = await loadAccessContext(); setAccessContext(context); return context;
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    configured: isSupabaseConfigured,
    localDemo,
    accessContext,
    accessToken,
    signIn: async (email, password, returnTo) => {
      try {
        if (localDemo) { setUser(demoUser); setAccessContext(demoAccess); return { error: null, destination: postLoginDestination(demoAccess, returnTo) }; }
        if (!isSupabaseConfigured) return { error: "Configura Supabase y el broker de sesión para iniciar sesión." };
        const session = await sessionBroker.signIn(email, password);
        const context = await acceptSession(session);
        return { error: null, destination: postLoginDestination(context, returnTo) };
      } catch (error) { clearSession(); return { error: error instanceof Error ? error.message : "No pudimos iniciar sesión." }; }
    },
    signOut: async () => {
      try { if (!localDemo && csrfToken) await sessionBroker.signOut(csrfToken); }
      finally { clearSession(); }
    },
    resetPassword: async (email) => {
      try { if (localDemo) return null; await sessionBroker.recover(email); return null; }
      catch (error) { return error instanceof Error ? error.message : "No pudimos enviar el enlace."; }
    },
    completeAuthCallback: async (tokenHash, type, returnTo) => {
      try {
        const session = await sessionBroker.verify(tokenHash, type);
        const context = await acceptSession(session);
        return { error: null, destination: authCallbackDestination(type, context, returnTo) };
      } catch (error) { clearSession(); return { error: error instanceof Error ? error.message : "El enlace ya no es válido." }; }
    },
    updatePassword: async (password) => {
      try {
        if (!csrfToken) return { error: "La sesión para crear la contraseña expiró." };
        await sessionBroker.updatePassword(password, csrfToken);
        const context = await refreshAccessContext();
        return { error: null, destination: postLoginDestination(context) };
      } catch (error) {
        return { error: error instanceof Error ? error.message : "No pudimos actualizar la contraseña." };
      }
    },
    resolveTenantAccess: async (slug) => {
      if (localDemo) {
        const workspace = demoAccess.workspaces.find((item) => item.slug === slug);
        return workspace ? { exists: true, organizationActive: workspace.organizationActive, membershipStatus: workspace.status, role: workspace.role, allowed: workspace.status === "ACTIVE" && workspace.organizationActive } : { exists: false, organizationActive: false, membershipStatus: null, role: null, allowed: false };
      }
      if (!supabase) return { exists: false, organizationActive: false, membershipStatus: null, role: null, allowed: false };
      const { data, error } = await supabase.rpc("get_tenant_status", { p_tenant_slug: slug });
      if (error) throw error;
      const result = (data ?? {}) as Record<string, unknown>;
      return {
        exists: result.exists === true,
        organizationActive: result.organization_active === true,
        membershipStatus: result.membership_status === "ACTIVE" || result.membership_status === "INVITED" || result.membership_status === "DISABLED" ? result.membership_status : null,
        role: result.role === "POSITRONICO_OWNER" || result.role === "CLIENT_ADMIN" || result.role === "CLIENT_MEMBER" ? result.role : null,
        allowed: result.allowed === true,
      };
    },
    refreshAccessContext,
    getMfaStatus: async () => {
      if (localDemo) return { aal: "aal2", factors: [{ id: "demo-factor", status: "verified", friendly_name: "Vista local" }] };
      if (!accessToken) throw new Error("La sesión expiró.");
      return sessionBroker.mfaStatus(accessToken);
    },
    enrollMfa: async () => {
      if (localDemo) return { factor_id: "demo-factor", qr_code: "" };
      if (!accessToken || !csrfToken) throw new Error("La sesión expiró.");
      return sessionBroker.enrollMfa(accessToken, csrfToken);
    },
    challengeMfa: async (factorId) => {
      if (localDemo) return "demo-challenge";
      if (!accessToken || !csrfToken) throw new Error("La sesión expiró.");
      const challenge = await sessionBroker.challengeMfa(factorId, accessToken, csrfToken);
      if (!challenge.id) throw new Error("No pudimos iniciar el desafío MFA.");
      return challenge.id;
    },
    verifyMfa: async (factorId, challengeId, code) => {
      try {
        if (localDemo) return { error: null, destination: "/admin" };
        if (!accessToken || !csrfToken) return { error: "La sesión expiró." };
        const session = await sessionBroker.verifyMfa(factorId, challengeId, code, accessToken, csrfToken);
        const context = await acceptSession(session);
        return { error: null, destination: postLoginDestination(context) };
      } catch (error) {
        if (error instanceof BrokerError && error.status === 401) return { error: "El código no es válido o ya expiró." };
        return { error: "No pudimos verificar el código. Inténtalo de nuevo." };
      }
    },
  }), [user, loading, accessContext, accessToken, csrfToken, acceptSession, clearSession, refreshAccessContext]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
